import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { EmptyState, PageHeader } from "../../components/dashboard/DashboardUI";
import { getCompanyValidSubscriptions } from "../../redux/actions/SubscriptionActions";
import { SERVICE_NAMES } from "../../utils/requestDisplay";
import { OHADA_COUNTRIES } from "../../utils/serviceFlows";

// Subscription services and their page in the client space
const SERVICES = [
  { code: "domiciliation", path: "/user/domiciliation" },
  { code: "admin", path: "/user/platformAdmin" },
  { code: "juridique", path: "/user/platformJuridique" },
  { code: "management", path: "/user/platformGest" },
  { code: "grh", path: "/user/platformGRH" },
  { code: "sales", path: "/user/platformSales" },
];

const isOffered = (code, country) => {
  if (code === "admin") return country === "CM";
  if (code === "domiciliation") return OHADA_COUNTRIES.includes(country);
  return true;
};

const formatDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "—");

function Subscriptions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { activeCompany } = useSelector((state) => state.companies);
  const { companyValidSubscriptions } = useSelector(
    (state) => state.subscriptions,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.token || !activeCompany?._id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.resolve(
      dispatch(getCompanyValidSubscriptions(user.token, activeCompany._id)),
    ).finally(() => setLoading(false));
  }, [user?.token, activeCompany?._id, dispatch]);

  const country = activeCompany?.country?.toUpperCase();
  const active = (companyValidSubscriptions || []).filter((sub) =>
    SERVICES.some((service) => service.code === sub.service),
  );
  const available = SERVICES.filter(
    (service) =>
      !active.some((sub) => sub.service === service.code) &&
      isOffered(service.code, country),
  );

  return (
    <>
      <PageHeader
        title="Abonnements"
        lead={
          activeCompany
            ? `Les services auxquels ${activeCompany.businessName} est abonnée.`
            : "Les services auxquels votre entreprise est abonnée."
        }
      />

      {!activeCompany ? (
        <div className="dash-card">
          <EmptyState title="Aucune entreprise active">
            Choisissez une entreprise en haut de la page pour voir ses
            abonnements.
          </EmptyState>
        </div>
      ) : loading ? (
        <p className="dash-loading">Chargement des abonnements…</p>
      ) : (
        <>
          <section className="dash-card">
            <h2 className="dash-card-title">Abonnements actifs</h2>
            {active.length === 0 ? (
              <EmptyState title="Aucun abonnement actif">
                Choisissez ci-dessous les services utiles à votre entreprise.
              </EmptyState>
            ) : (
              <div className="dash-table-wrap">
                <table className="dash-table is-responsive">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Formule</th>
                      <th>Début</th>
                      <th>Fin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.map((sub) => (
                      <tr key={sub._id || sub.id || sub.service}>
                        <td>
                          <span className="dash-cell-title">
                            {SERVICE_NAMES[sub.service] || sub.service}
                          </span>
                        </td>
                        <td data-label="Formule">
                          {sub.billing === "yearly"
                            ? "Annuelle"
                            : sub.billing === "monthly"
                              ? "Mensuelle"
                              : "Unique"}
                        </td>
                        <td data-label="Début">{formatDate(sub.startsOn)}</td>
                        <td data-label="Fin">
                          {sub.endsOn ? formatDate(sub.endsOn) : "Sans fin"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {available.length > 0 && (
            <section className="dash-card dash-section">
              <h2 className="dash-card-title">Autres services disponibles</h2>
              <ul className="dash-list">
                {available.map((service) => (
                  <li key={service.code}>
                    <span className="dash-cell-title">
                      {SERVICE_NAMES[service.code]}
                    </span>
                    <button
                      type="button"
                      className="dash-link-button"
                      onClick={() => navigate(service.path)}
                    >
                      Découvrir
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </>
  );
}

export default Subscriptions;
